import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileUp, Laptop, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { downloadFile } from "@/components/files/FilePreview";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

export const APP_NAMES = { word: "Word", sheet: "Excel", slides: "PowerPoint" };

export function NewVersionButton({ file, onDone, label = "Déposer une nouvelle version", testId = "upload-version-btn" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (f) => {
    if (!f) return;
    setBusy(true);
    const form = new FormData(); form.append("file", f);
    try { const { data } = await api.post(`/files/${file.id}/versions`, form); toast.success(`Version ${data.version} enregistrée`); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" className="hidden" accept={file.ext ? `.${file.ext}` : undefined} onChange={(e) => send(e.target.files?.[0])} data-testid={`${testId}-input`} />
      <Button variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()} data-testid={testId}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileUp className="h-4 w-4 mr-1.5" />}{label}
      </Button>
    </>
  );
}

export function OfficeEditDialog({ file, kind, open, onOpenChange, onDone }) {
  const [syncPath, setSyncPath] = useState(null);
  useEffect(() => {
    if (open) api.get(`/files/${file.id}/sync-path`).then((r) => setSyncPath(r.data.path)).catch(() => setSyncPath(""));
  }, [open, file.id]);
  const app = APP_NAMES[kind];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="office-edit-dialog">
        <DialogTitle className="font-display">Modifier dans {app}</DialogTitle>
        <DialogDescription>Le document s'ouvre dans {app} installé sur votre PC ; chaque enregistrement crée une nouvelle version, l'historique est conservé.</DialogDescription>
        <div className="border rounded-lg p-4 space-y-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Laptop className="h-4 w-4 text-[#1E3A8A]" />Avec la synchronisation PC</div>
          {syncPath ? <p className="text-sm text-slate-600">Ouvrez <code className="bg-slate-100 px-1 rounded break-all" data-testid="office-sync-path">SDR PNEUS 91\{syncPath}</code> sur votre PC. Vos enregistrements sont envoyés automatiquement.</p>
            : <p className="text-sm text-slate-600">Installez l'outil depuis <a href="/compte" className="text-[#1E3A8A] underline">Mon compte &gt; Synchronisation PC</a> : les fichiers modifiés seront renvoyés tout seuls.</p>}
        </div>
        <div className="border rounded-lg p-4 space-y-3">
          <div className="text-sm font-semibold">Sans synchronisation</div>
          <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1">
            <li>Téléchargez le fichier et ouvrez-le dans {app}.</li>
            <li>Modifiez-le puis enregistrez-le.</li>
            <li>Déposez le fichier enregistré ici.</li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => downloadFile(file)} data-testid="office-download-btn"><Download className="h-4 w-4 mr-1.5" />1. Télécharger</Button>
            <NewVersionButton file={file} label="3. Déposer la version modifiée" testId="office-upload-version-btn" onDone={() => { onOpenChange(false); onDone?.(); }} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
